import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { Box } from '@mui/material';
import {
  parsePresentationVideoOverlays,
  presentationVideoCropBox,
  presentationVideosOnFrame,
  resolvePresentationVideoTransition,
  type PresentationVideoOverlay,
} from '@/helpers/preview/presentation-video-overlays.helper';
import {
  previewVideoSeekSec,
  previewVideoStartDelayMs,
  rememberLocalClipStart,
  type SequenceFrameHistory,
} from '@/helpers/preview/sequence-step-hold.helper';

/**
 * Embedded presentation videos over their posters. They start muted so autoplay is allowed.
 * The preview listen button sets `audible` and unmutes a clip that is already playing.
 * In live playout a clip that starts on this step waits for the same server time the
 * players use, then stays on that clock.
 */
export function PreviewPresentationVideoOverlays({
  raw,
  frameIndex,
  audible = false,
  startedAtByVideoId,
  serverNowMs,
  clipHistoryRef,
  clipMediaKey,
}: {
  raw?: string;
  frameIndex: number;
  audible?: boolean;
  /** Live playout: server stamp of the step that started each clip. Local previews omit this and start at the trim. */
  startedAtByVideoId?: Record<string, number>;
  serverNowMs?: () => number;
  /** Local autoplay: the hold reads the clip start this overlay writes when playback begins. */
  clipHistoryRef?: RefObject<SequenceFrameHistory | null>;
  clipMediaKey?: string;
}) {
  const overlays = useMemo(() => parsePresentationVideoOverlays(raw), [raw]);
  const videos = presentationVideosOnFrame(overlays, frameIndex);
  if (!overlays || videos.length === 0) return null;
  const ratio = overlays.aspectRatio;

  return (
    <Box sx={{ position: 'absolute', inset: 0, containerType: 'size', pointerEvents: 'none', zIndex: 1 }}>
      <Box
        sx={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          // Same box as the contain-fitted slide image.
          width: `min(100cqw, calc(100cqh * ${ratio}))`,
          height: `min(100cqh, calc(100cqw / ${ratio}))`,
        }}
      >
        {videos.map((video) => (
          <PreviewOverlayVideo
            key={`${video.id}:${video.fromFrame}`}
            video={video}
            frameIndex={frameIndex}
            audible={audible}
            startedAtMs={startedAtByVideoId?.[video.id]}
            serverNowMs={serverNowMs}
            clipHistoryRef={clipHistoryRef}
            clipMediaKey={clipMediaKey}
          />
        ))}
      </Box>
    </Box>
  );
}

type StartOrder = { delayMs: number; seekToSec: number; startedAtMs?: number } | null;

function PreviewOverlayVideo({
  video,
  frameIndex,
  audible,
  startedAtMs,
  serverNowMs,
  clipHistoryRef,
  clipMediaKey,
}: {
  video: PresentationVideoOverlay;
  frameIndex: number;
  audible: boolean;
  startedAtMs?: number;
  serverNowMs?: () => number;
  clipHistoryRef?: RefObject<SequenceFrameHistory | null>;
  clipMediaKey?: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [seenFrame, setSeenFrame] = useState<number | null>(null);
  const [order, setOrder] = useState<StartOrder>(null);
  const [playingFor, setPlayingFor] = useState<StartOrder>(null);
  const trimStartSec = video.trimStartMs / 1000;
  const seekToSec = (startedAt: number | undefined) =>
    startedAt != null && serverNowMs
      ? previewVideoSeekSec({
          startedAtMs: startedAt,
          serverNowMs: serverNowMs(),
          startDelayMs: video.startDelayMs,
          trimStartMs: video.trimStartMs,
          trimFromEndMs: video.trimFromEndMs,
          durationMs: video.durationMs,
        })
      : trimStartSec;
  const liveClipStart = Boolean(serverNowMs) && video.startFrame === frameIndex;
  if (seenFrame !== frameIndex) {
    setSeenFrame(frameIndex);
    const transition = resolvePresentationVideoTransition(video.startFrame, seenFrame ?? undefined, frameIndex);
    if (transition === 'reset') {
      setOrder(null);
    } else if (transition !== 'keep') {
      if (liveClipStart && startedAtMs == null) {
        setOrder(null);
      } else {
        setOrder({
          delayMs:
            startedAtMs != null && serverNowMs
              ? previewVideoStartDelayMs({
                  startedAtMs,
                  startDelayMs: video.startDelayMs,
                  serverNowMs: serverNowMs(),
                })
              : transition === 'startDelayed'
                ? video.startDelayMs
                : 0,
          seekToSec: seekToSec(startedAtMs),
          startedAtMs,
        });
      }
    }
  } else if (startedAtMs != null && order?.startedAtMs !== startedAtMs && (serverNowMs || order)) {
    setOrder({
      delayMs: serverNowMs
        ? previewVideoStartDelayMs({
            startedAtMs,
            startDelayMs: video.startDelayMs,
            serverNowMs: serverNowMs(),
          })
        : 0,
      seekToSec: seekToSec(startedAtMs),
      startedAtMs,
    });
  }
  const audibleRef = useRef(audible);

  useEffect(() => {
    const clipHistory = clipHistoryRef?.current;
    if (serverNowMs || !clipHistory || !clipMediaKey || video.startFrame == null || frameIndex <= video.startFrame) {
      return;
    }
    rememberLocalClipStart(clipHistory, clipMediaKey, frameIndex, Date.now(), [
      {
        id: video.id,
        toFrame: video.toFrame,
        startFrame: video.startFrame,
        loop: false,
        startDelayMs: video.startDelayMs,
        durationMs: video.durationMs,
      },
    ]);
  }, [clipHistoryRef, clipMediaKey, frameIndex, serverNowMs, video]);

  useEffect(() => {
    audibleRef.current = audible;
    const element = videoRef.current;
    if (!element) return;
    // Unmute a clip that is already playing. Starting playback stays muted so autoplay is allowed.
    element.muted = !audible;
    if (audible) element.volume = 1;
  }, [audible]);

  useEffect(() => {
    const element = videoRef.current;
    if (!element) return;
    // React does not reliably apply the muted attribute, and an unmuted play() is rejected.
    element.muted = true;
    if (!order) {
      element.pause();
      if (element.readyState >= HTMLMediaElement.HAVE_METADATA) element.currentTime = trimStartSec;
      return;
    }
    const startPlayback = () => {
      const target = order.seekToSec;
      if (Math.abs(element.currentTime - target) > 0.05) {
        element.currentTime = target;
      }
      element
        .play()
        ?.then(() => {
          if (!audibleRef.current) return;
          element.muted = false;
          element.volume = 1;
        })
        .catch(() => {
          // Autoplay rejections: the poster stays visible.
        });
    };
    const timer = window.setTimeout(() => {
      if (element.readyState >= HTMLMediaElement.HAVE_METADATA) {
        startPlayback();
        return;
      }
      element.addEventListener('loadedmetadata', startPlayback, { once: true });
      if (element.readyState >= HTMLMediaElement.HAVE_METADATA) {
        element.removeEventListener('loadedmetadata', startPlayback);
        startPlayback();
      }
    }, order.delayMs);
    return () => {
      window.clearTimeout(timer);
      element.removeEventListener('loadedmetadata', startPlayback);
    };
  }, [order, trimStartSec]);

  useEffect(() => {
    if (!order?.startedAtMs || !serverNowMs) return;
    const element = videoRef.current;
    if (!element) return;
    const correct = () => {
      if (element.readyState < HTMLMediaElement.HAVE_METADATA) return;
      const target = previewVideoSeekSec({
        startedAtMs: order.startedAtMs,
        serverNowMs: serverNowMs(),
        startDelayMs: video.startDelayMs,
        trimStartMs: video.trimStartMs,
        trimFromEndMs: video.trimFromEndMs,
        durationMs: video.durationMs,
      });
      if (Math.abs(element.currentTime - target) > 0.25) {
        element.currentTime = target;
      }
    };
    const timer = window.setInterval(correct, 1000);
    return () => window.clearInterval(timer);
  }, [order, serverNowMs, video]);

  return (
    <Box
      sx={{
        position: 'absolute',
        left: `${video.x * 100}%`,
        top: `${video.y * 100}%`,
        width: `${video.width * 100}%`,
        height: `${video.height * 100}%`,
        transform: video.rotation ? `rotate(${video.rotation}deg)` : undefined,
        overflow: 'hidden',
        opacity: order !== null && playingFor === order ? 1 : 0,
      }}
    >
      <video
        ref={videoRef}
        src={video.url}
        preload="auto"
        playsInline
        muted={!audible}
        onPlaying={() => setPlayingFor(order)}
        onTimeUpdate={() => {
          const element = videoRef.current;
          if (!element || !Number.isFinite(element.duration)) return;
          const endSec =
            video.durationMs && video.durationMs > 0
              ? Math.max(0, (video.durationMs - video.trimFromEndMs) / 1000)
              : video.trimFromEndMs > 0
                ? element.duration - video.trimFromEndMs / 1000
                : undefined;
          if (endSec == null || element.currentTime < endSec) return;
          element.pause();
          if (video.hideWhenStopped) setPlayingFor(null);
        }}
        onEnded={() => {
          if (video.hideWhenStopped) setPlayingFor(null);
        }}
        style={{ position: 'absolute', ...presentationVideoCropBox(video.crop), objectFit: 'fill', display: 'block' }}
      />
    </Box>
  );
}
